import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  TextFieldProps,
  Typography,
  useTheme,
} from '@mui/material';
import * as libphonenumber from 'google-libphonenumber';
import { SearchInput } from 'src/components/search_input';
import { countryList } from 'src/constants/countries';

// Country Selection Menu Component - manages its own search state
interface CountrySelectionMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  selectedRegion: string;
  onRegionSelect: (region: string) => void;
  getFlagImagePath: (regionCode: string) => string;
}

const CountrySelectionMenu: FC<CountrySelectionMenuProps> = ({
  anchorEl,
  open,
  onClose,
  selectedRegion,
  onRegionSelect,
  getFlagImagePath,
}) => {
  const [searchText, setSearchText] = useState('');

  // Filter countries based on search text
  const filteredCountries = useMemo(() => {
    if (!searchText.trim()) {
      return countryList;
    }

    const searchLower = searchText.toLowerCase();
    return countryList.filter(
      (country) =>
        country.en_short_name.toLowerCase().includes(searchLower) ||
        country.alpha_2_code.toLowerCase().includes(searchLower) ||
        country.dial_code.includes(searchText),
    );
  }, [searchText]);

  const handleClose = useCallback(() => {
    setSearchText(''); // Reset search when closing
    onClose();
  }, [onClose]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            maxHeight: 400,
            width: 320,
          },
        },
      }}
    >
      {/* Search Input */}
      <Box
        sx={{
          m: 1,
        }}
      >
        <SearchInput
          fullWidth
          placeholder='Search countries...'
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </Box>
      {/* Country List */}
      <Box
        sx={{
          maxHeight: 300,
          overflow: 'auto',
        }}
      >
        {filteredCountries.length === 0 ? (
          <Box
            sx={{
              p: 2,
              textAlign: 'center',
            }}
          >
            <Typography
              variant='body2'
              sx={{
                color: 'text.secondary',
              }}
            >
              No countries found
            </Typography>
          </Box>
        ) : (
          filteredCountries.map((country) => (
            <MenuItem
              key={country.alpha_2_code}
              onClick={() => onRegionSelect(country.alpha_2_code)}
              selected={country.alpha_2_code === selectedRegion}
              sx={{ py: 1 }}
            >
              <Box
                component='img'
                src={getFlagImagePath(country.alpha_2_code)}
                alt={country.en_short_name}
                sx={{
                  width: 24,
                  height: 18,
                  mr: 1.5,
                }}
              />
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Typography variant='body2'>{country.en_short_name}</Typography>
                <Typography
                  variant='caption'
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {country.dial_code}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Box>
    </Menu>
  );
};

interface PhoneNumberInputState {
  e164: string | null;
  formatted: string | null;
  isValid: boolean;
  regionCode: string;
}

export interface PhoneNumberInputProps extends Omit<
  TextFieldProps,
  'onChange' | 'value'
> {
  value?: string;
  onChange: (state: PhoneNumberInputState) => void; // Callback with E.164 formatted number
  defaultRegionCode?: string; // Default region code (defaults to 'US')
}

export const PhoneNumberInput: FC<PhoneNumberInputProps> = ({
  value,
  onChange,
  defaultRegionCode: defaultRegion = 'US',
  ...textFieldProps
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRegion, setSelectedRegion] = useState<string>(defaultRegion);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingCursorRef = useRef<number | null>(null);

  const isMenuOpen = Boolean(anchorEl);
  const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();
  const countryCode = phoneUtil.getCountryCodeForRegion(selectedRegion);
  const formattedValue = useMemo(
    () =>
      formatDigitsAsNational(
        value?.replace(/[^0-9]/g, '') ?? '',
        selectedRegion,
        phoneUtil,
      ),
    [phoneUtil, selectedRegion, value],
  );

  // Get country by region code
  const getCountryByRegion = useCallback((region: string) => {
    return countryList.find((country) => country.alpha_2_code === region);
  }, []);

  // Get flag image path
  const getFlagImagePath = useCallback((regionCode: string) => {
    // Flag files are lowercase, so convert region code to lowercase
    return `/flags/${regionCode.toLowerCase()}_cc.png`;
  }, []);

  // Handle phone number input change - format as user types and notify parent with E.164
  const handlePhoneNumberChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.target;
      const cursorPosition = input.selectionStart ?? 0;
      const text = input.value;

      // If input is empty, notify parent
      if (!text || text.trim() === '') {
        onChange({
          e164: null,
          formatted: '',
          isValid: false,
          regionCode: selectedRegion,
        });
        return;
      }

      const digitsOnly = text.replace(/\D/g, '');

      // Count how many digits are before the cursor (so we can restore cursor after formatting)
      const digitsBeforeCursor = (
        text.slice(0, cursorPosition).match(/\d/g) ?? []
      ).length;

      const parsed = parseAndValidatePhoneNumber(
        digitsOnly,
        selectedRegion,
        phoneUtil,
      );

      const newValue = formatDigitsAsNational(
        digitsOnly,
        selectedRegion,
        phoneUtil,
      );

      // Only pass e164 when valid; for partial input the parent should use formatted for display
      onChange({
        e164: parsed.isValid ? parsed.e164 : newValue,
        formatted: newValue,
        isValid: parsed.isValid,
        regionCode: selectedRegion,
      });

      // Cursor position in the formatted string we're displaying
      const newCursorPos = (() => {
        if (digitsBeforeCursor === 0) return 0;
        let digitCount = 0;
        for (let i = 0; i < newValue.length; i++) {
          if (/\d/.test(newValue[i])) {
            digitCount++;
            if (digitCount === digitsBeforeCursor) {
              return i + 1;
            }
          }
        }
        return newValue.length;
      })();
      // Store cursor to apply after React commits. Setting it here gets overwritten when React
      // updates the controlled input's value (cursor resets to end), causing the next keystroke
      // to insert in the wrong place.
      pendingCursorRef.current = newCursorPos;
    },
    [selectedRegion, phoneUtil, onChange],
  );

  // Apply cursor after React has committed the new value so it isn't reset to end.
  useEffect(() => {
    if (pendingCursorRef.current === null) return;
    const el = inputRef.current;
    if (el && document.activeElement === el) {
      const pos = Math.min(pendingCursorRef.current, el.value.length);
      pendingCursorRef.current = null;
      el.setSelectionRange(pos, pos);
    } else {
      pendingCursorRef.current = null;
    }
  }, [formattedValue]);

  // Handle region selection
  const handleRegionSelect = useCallback(
    (region: string) => {
      setSelectedRegion(region);
      setAnchorEl(null);
      pendingCursorRef.current = null;

      onChange({
        e164: null,
        formatted: formattedValue,
        isValid: false,
        regionCode: region,
      });
    },
    [onChange, formattedValue],
  );

  // Handle flag button click
  const handleFlagClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  // Handle menu close
  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const selectedCountry = getCountryByRegion(selectedRegion);

  return (
    <>
      <TextField
        {...textFieldProps}
        value={formattedValue}
        onChange={handlePhoneNumberChange}
        inputRef={inputRef}
        onFocus={(e) => {
          setIsFocused(true);
          textFieldProps.onFocus?.(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          textFieldProps.onBlur?.(e);
        }}
        slotProps={{
          ...textFieldProps.slotProps,
          inputLabel: {
            ...textFieldProps.slotProps?.inputLabel,
            shrink: !!(value && value.trim()) || isFocused,
          },
          htmlInput: {
            maxLength: 15,
          },
          input: {
            ...textFieldProps.slotProps?.input,
            startAdornment: (
              <InputAdornment position='start'>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <IconButton
                    onClick={handleFlagClick}
                    edge='start'
                    sx={{ px: 1 }}
                    aria-label='Select country'
                  >
                    {selectedCountry ? (
                      <Box
                        component='img'
                        src={getFlagImagePath(selectedCountry.alpha_2_code)}
                        alt={selectedCountry.en_short_name}
                        sx={{
                          width: 35.5,
                          height: 20,
                        }}
                      />
                    ) : (
                      // Fallback: show US flag if country not found
                      <Box
                        component='img'
                        src={getFlagImagePath('US')}
                        alt='United States'
                        sx={{
                          width: 35.5,
                          height: 20,
                        }}
                      />
                    )}
                  </IconButton>
                  <IconButton
                    onClick={handleFlagClick}
                    edge='start'
                    sx={{ p: 0.25 }}
                    aria-label='Select country'
                  >
                    {isMenuOpen ? (
                      <KeyboardArrowUpIcon
                        sx={{
                          fontSize: 20,
                          color: theme.palette.text.secondary,
                        }}
                      />
                    ) : (
                      <KeyboardArrowDownIcon
                        sx={{
                          fontSize: 20,
                          color: theme.palette.text.secondary,
                        }}
                      />
                    )}
                  </IconButton>
                  <Typography variant='body2' sx={{ justifySelf: 'flex-end' }}>
                    +{countryCode}
                  </Typography>
                </Box>
              </InputAdornment>
            ),
          },
        }}
        sx={{
          ...textFieldProps.sx,
          // Shift label to the right when not shrunk to avoid overlap with startAdornment
          '& .MuiInputLabel-root': {
            ...(!(value && value.trim()) && !isFocused
              ? {
                  left: 70 + 10 * countryCode.toString().length,
                }
              : {}),
          },
        }}
      />
      {/* Country Selection Menu */}
      <CountrySelectionMenu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        selectedRegion={selectedRegion}
        onRegionSelect={handleRegionSelect}
        getFlagImagePath={getFlagImagePath}
      />
    </>
  );
};

/**
 * Format digits as national format only when valid.
 * Partial/invalid input is returned as raw digits so we don't show patterns like "951-7906".
 */
function formatDigitsAsNational(
  digitsOnly: string,
  region: string,
  phoneUtil: libphonenumber.PhoneNumberUtil,
): string {
  if (!digitsOnly) return '';
  try {
    const phoneNumber = phoneUtil.parseAndKeepRawInput(digitsOnly, region);
    if (!phoneUtil.isValidNumber(phoneNumber)) return digitsOnly;
    return phoneUtil.format(
      phoneNumber,
      libphonenumber.PhoneNumberFormat.NATIONAL,
    );
  } catch {
    return digitsOnly;
  }
}

/**
 * Parses and validates a phone number.
 * @param phoneNumber - The phone number to parse and validate.
 * @param region - The region code to use for parsing.
 * @param phoneUtil - The phone number util to use for parsing.
 * @returns The E164 formatted phone number and whether the number is valid.
 */
const parseAndValidatePhoneNumber = (
  phoneNumber: string,
  region: string,
  phoneUtil: libphonenumber.PhoneNumberUtil,
): {
  isValid: boolean;
  e164: string | null;
  phoneNumber: libphonenumber.PhoneNumber | null;
} => {
  if (!phoneNumber) {
    return {
      isValid: false,
      e164: null,
      phoneNumber: null,
    };
  }

  // Remove all non-numeric characters
  const digitsOnly = phoneNumber.replace(/[^0-9]/g, '');

  if (!digitsOnly) {
    return {
      isValid: false,
      e164: null,
      phoneNumber: null,
    };
  }

  try {
    // Parse as national number with the selected region
    const parsedPhoneNumber = phoneUtil.parseAndKeepRawInput(
      digitsOnly,
      region,
    );
    const isValid = phoneUtil.isValidNumber(parsedPhoneNumber);
    const e164 = phoneUtil.format(
      parsedPhoneNumber,
      libphonenumber.PhoneNumberFormat.E164,
    );
    return {
      isValid,
      e164,
      phoneNumber: parsedPhoneNumber,
    };
  } catch {
    // If parsing fails, try to create E.164 anyway for partial numbers
    // This allows the parent to update and display the formatted input
    try {
      // Try parsing with the region code prefix
      const countryCode = phoneUtil.getCountryCodeForRegion(region);
      if (countryCode && digitsOnly) {
        return {
          isValid: false,
          e164: digitsOnly,
          phoneNumber: null,
        };
      }
    } catch {
      // If that fails too, return invalid
    }
    return {
      isValid: false,
      e164: null,
      phoneNumber: null,
    };
  }
};
